import React, { useMemo } from 'react';

import classnames from 'classnames';
import ColorContrastChecker from 'color-contrast-checker';

import { useThemeColorScheme } from 'public/components/default_template/meta/useTheme';

import ChevronIcon from './ChevronIcon';
import { CarouselDirection } from './useCarousel';

type ArrowProps = {
  onClickScroll: (direction: CarouselDirection) => void;
  currentItem: number;
  whiteInactiveArrow?: boolean;
  color: string;
  totalScrollItems: number;
};

export const CarouselArrowLeft = ({ onClickScroll, currentItem, whiteInactiveArrow, color }: ArrowProps) =>
  <button aria-label="scroll left" className={classnames('carouselArrow left', { inactive: currentItem === 0 })} onClick={() => onClickScroll('backwards')} data-testid="carousel-left-arrow">
    <ChevronIcon color={currentItem === 0 ? whiteInactiveArrow && 'white' || color : color} />
  </button>;

export const CarouselArrowRight = ({ onClickScroll, currentItem, whiteInactiveArrow, color, totalScrollItems }: ArrowProps) =>
  <button
    aria-label="scroll right" className={classnames('carouselArrow right', { inactive: currentItem === totalScrollItems - 1 })}
    onClick={() => onClickScroll('forwards')} data-testid="carousel-right-arrow">
    <ChevronIcon color={currentItem === totalScrollItems - 1 ? whiteInactiveArrow && 'white' || color : color} />
  </button>;


type Props = {
  arrowsRef: React.RefObject<HTMLDivElement>;
  color: string;
  whiteInactiveArrow?: boolean;
  arrowSize?: 'small' | 'large';
  onClickScroll: (direction: CarouselDirection) => void;
  currentItem: number;
  totalScrollItems: number;
  textColor?: string;
};

const CarouselArrows = (props: Props) => {
  const { arrowsRef, arrowSize, ...arrowProps } = props;
  const getColorFromTheme = useThemeColorScheme();
  const color = useMemo(() => {
    const contrastChecker = new ColorContrastChecker();
    return getColorFromTheme(theme => theme.colorScheme.icon.default, ['#FFFFFF', '#000000'].filter(color => contrastChecker.isLevelAA(props.color, color))[0] || '#FFFFFF');
  }, [getColorFromTheme, props.color]);
  const colorsStyle = props?.textColor
    ? { color: props.textColor }
    : {};

  return (
    <div className={classnames('carouselArrows', arrowSize, { whiteBorder: arrowProps.whiteInactiveArrow })} ref={arrowsRef} data-testid="carousel-arrows">
      <CarouselArrowLeft {...arrowProps} color={color} />
      <div
        className="itemRange"
        aria-label={`Currently on slide ${arrowProps.currentItem + 1} of ${arrowProps.totalScrollItems}`}
        style={colorsStyle}>
        {arrowProps.currentItem + 1}
        <span className="itemTotal"> / {arrowProps.totalScrollItems}</span>
      </div>
      <CarouselArrowRight {...arrowProps} color={color} />
    </div>
  );
};

export default CarouselArrows;
